/**
 * 전역 PDF/이미지 미리보기 모달 상태.
 *
 * 컴포넌트 트리 밖(예: attachment-api 의 plain 함수)에서도 호출할 수 있도록
 * React state 가 아닌 외부 store 패턴(useSyncExternalStore)으로 구현.
 * 어디서든 openPdfPreview() 만 부르면 App 에 한 번 마운트된 PdfPreviewModal 이 뜬다.
 */

import type { MailComposeInit } from './mail-compose';
import { isSheetFile } from './sheet-preview';

/**
 * 미리보기 안에서 바로 결정하게 하는 확인 막대 (v2.52.0 — 지출결재 제출 전 확인).
 * 지정하지 않으면 지금까지처럼 보기 전용 미리보기.
 */
export interface PdfPreviewConfirm {
  /** 확인 막대에 띄울 안내 문구 */
  message?: string;
  /** 실행 버튼 라벨 (예: '제출') */
  confirmLabel: string;
  /** 되돌아가기 버튼 라벨 (기본 '돌아가서 수정') */
  cancelLabel?: string;
  /** 실행 — Promise 를 돌려주면 끝날 때까지 버튼이 잠긴다 */
  onConfirm: () => void | Promise<void>;
}

export interface PdfPreviewState {
  open: boolean;
  /** 인라인 렌더 URL (iframe/img src) — 서버가 Content-Disposition: inline 으로 응답 */
  url: string;
  /** 강제 다운로드 URL */
  downloadUrl: string;
  filename: string;
  /** 이미지면 <img>, 아니면 <iframe> 로 렌더 */
  isImage: boolean;
  /** 📊 엑셀(xlsx·xlsm·xls·csv) — 받아서 화면에서 표로 그린다 (v2.270.0 · components/SheetPreview) */
  isSheet: boolean;
  /** 받은 형식(mime) — CSV 글자 풀기에 쓴다 */
  mime?: string;
  /** PDF/이미지/엑셀처럼 화면에서 보여 줄 수 있는 형식인지 */
  canPreview: boolean;
  /** 저장 버튼 라벨 (기본 '저장') — 미리보기와 다른 형식을 내려줄 때 명시 (예: '엑셀 다운로드') */
  downloadLabel?: string;
  /** 미리보기 안에서 바로 결정하게 할 때 (v2.52.0) */
  confirm?: PdfPreviewConfirm;
  /** 지정하면 헤더에 ✉ 메일 버튼 — 이 문서를 첨부한 메일 작성 모달을 연다 (v2.108.0) */
  mailDoc?: MailComposeInit;
  /**
   * 저장 단추를 감춘다 — 아직 만들지 않은 문서의 미리보기(v2.292.0 · 정산서 👁 미리보기).
   * 여기서 내려받으면 문서함 보관 없이 파일만 생겨 「만든 문서는 늘 보관」이 깨진다 → 확인 막대의 [문서작성]으로만.
   */
  noDownload?: boolean;
}

const CLOSED: PdfPreviewState = {
  open: false,
  url: '',
  downloadUrl: '',
  filename: '',
  isImage: false,
  isSheet: false,
  mime: undefined,
  canPreview: false,
  downloadLabel: undefined,
  confirm: undefined,
  mailDoc: undefined,
  noDownload: undefined,
};

let state: PdfPreviewState = CLOSED;
const listeners = new Set<() => void>();

function emit() {
  for (const fn of listeners) fn();
}

export function subscribePdfPreview(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function getPdfPreviewState(): PdfPreviewState {
  return state;
}

export function openPdfPreview(opts: {
  url: string;
  downloadUrl: string;
  filename: string;
  /** 선택 — 있으면 형식 판정에 함께 사용 */
  mime?: string;
  /** 선택 — 저장 버튼 라벨 (기본 '저장') */
  downloadLabel?: string;
  /** 선택 — 미리보기 안에서 바로 결정 (예: 지출결재 제출) */
  confirm?: PdfPreviewConfirm;
  /** 선택 — 이 문서를 메일로 보낼 수 있게 (헤더 ✉ 버튼, v2.108.0) */
  mailDoc?: MailComposeInit;
  /** 선택 — 저장 단추 감춤 (아직 만들지 않은 문서의 미리보기 · v2.292.0) */
  noDownload?: boolean;
  /** 선택 — 이름이 그림·PDF 처럼 보여도 미리보기를 하지 않는다(「미리보기 없음 + 저장」 · v2.313.0 NAS 의 svg 등) */
  noPreview?: boolean;
}): void {
  const lower = (opts.filename || '').toLowerCase();
  const mime = (opts.mime ?? '').toLowerCase();
  const isImage = !opts.noPreview && (/\.(png|jpe?g|gif|webp|bmp|svg)$/.test(lower) || mime.startsWith('image/'));
  const isPdf = !opts.noPreview && (/\.pdf$/.test(lower) || mime.includes('pdf'));
  const isSheet = !opts.noPreview && !isImage && !isPdf && isSheetFile(opts.filename, mime);
  state = {
    open: true,
    url: opts.url,
    downloadUrl: opts.downloadUrl,
    filename: opts.filename,
    isImage,
    isSheet,
    mime: opts.mime,
    canPreview: isImage || isPdf || isSheet,
    downloadLabel: opts.downloadLabel,
    confirm: opts.confirm,
    mailDoc: opts.mailDoc,
    noDownload: opts.noDownload,
  };
  emit();
}

export function closePdfPreview(): void {
  if (!state.open) return;
  state = CLOSED;
  emit();
}

/**
 * 서버에서 생성하는 PDF(견적서·발주서·기술지원확약서 등)를 팝업 미리보기로.
 * 해당 엔드포인트에 ?inline=1 을 붙여 인라인 표시, 저장 버튼은 원본 URL(다운로드) 사용.
 * downloadUrl/downloadLabel 을 주면 저장 버튼이 미리보기와 다른 파일을 내려줌
 * (예: 과거 판보 — 미리보기는 PDF, 저장은 원본 엑셀).
 */
export function openServerPdfPreview(opts: {
  pdfUrl: string;
  filename: string;
  downloadUrl?: string;
  downloadLabel?: string;
  /** 선택 — 이 문서를 메일로 보낼 수 있게 (헤더 ✉ 버튼, v2.108.0) */
  mailDoc?: MailComposeInit;
}): void {
  const sep = opts.pdfUrl.includes('?') ? '&' : '?';
  openPdfPreview({
    url: `${opts.pdfUrl}${sep}inline=1`,
    downloadUrl: opts.downloadUrl ?? opts.pdfUrl,
    filename: opts.filename,
    mime: 'application/pdf',
    downloadLabel: opts.downloadLabel,
    mailDoc: opts.mailDoc,
  });
}
